import React from 'react';
import { Card } from 'react-bootstrap';
import { styles, LoadingSpinner } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import moment from 'moment';
import { useOktaQuery, Schedule } from '../../data';

const DayHeader = styled.div`
    background-color: ${styles.theme.background.geyser};
    color: ${styles.theme.fontColors.mineShaft};
    text-transform: uppercase;
    margin: 0;
    font-family: ${styles.theme.font.body};
    padding: 6px 10px;
    font-weight: bold;
    font-size: 1.25em;
    @media (min-width: 992px) {
        position: sticky;
        top: 0;
    }
`;
const DaySchedule = styled.ul`
    padding: 0 !important;
    li {
        border-bottom: 1px solid #dee2e6;
    }

    li:last-child {
        border-bottom: none;
    }
`;
const DayEventMarker = styled.div`
    width: 0.25rem;
    background-color: ${(props) => props.color};
`;

const DAILY_SCHEDULE_TYPE = {
    joint: 0,
    pitcher: 1,
    positionPlayer: 2,
};
const DAILY_SCHEDULE_ACTIVITY_TYPE = {
    infielders: 0,
    outfielders: 1,
    catchers: 2,
    throwingPitchers: 3,
    nonThrowingPitchers: 4,
};

const querySchedule = async (accessToken, teamId, playerId) => Schedule.getUpcomingSchedule(accessToken, { teamId, playerId });

const TODAY = moment().startOf('d');

const orderedActivities = (activities) => [...activities].sort((a, b) => a.StartTime > b.StartTime);

const formatTime = (time) => {
    const date = moment(time, 'HH:mm:ss');
    // eslint-disable-next-line no-underscore-dangle
    return date._isValid ? date.format('h:mm A') : '';
};

const getWorkoutsForDate = (workouts, date) => workouts.filter((w) => date.isSame(moment(w.ScheduledDate)));
const getActivitiesForDate = (schedules, day, playerId, isPitcher, isCatcher) => {
    const filteredSchedules = schedules.filter((s) => s.Type === DAILY_SCHEDULE_TYPE.joint
        || (s.Type === DAILY_SCHEDULE_TYPE.pitcher && isPitcher)
        || (s.Type === DAILY_SCHEDULE_TYPE.positionPlayer && !isPitcher));
    const activitiesOnDay = filteredSchedules.reduce((all, s) => [...all, ...s.DailyScheduleActivities], [])
        .filter((a) => a.IsNextDayActivity === day.isTomorrow);

    return activitiesOnDay.filter((a) => (a.ActivityType === null
        || (isPitcher && (a.ActivityType === DAILY_SCHEDULE_ACTIVITY_TYPE.throwingPitchers
            || a.ActivityType === DAILY_SCHEDULE_ACTIVITY_TYPE.nonThrowingPitchers))
        || (isCatcher && (a.ActivityType === DAILY_SCHEDULE_ACTIVITY_TYPE.catchers))
        || (!isPitcher && !isCatcher && (a.ActivityType === DAILY_SCHEDULE_ACTIVITY_TYPE.infielders
            || a.ActivityType === DAILY_SCHEDULE_ACTIVITY_TYPE.outfielders)))
        && (!a.ActivityGroups.length || !!a.ActivityGroups.find((ag) => ag.ActivityAssignments.find((aa) => aa.PlayerID === playerId))));
};
const getActivityGroupName = (activity, playerId) => {
    const group = activity.ActivityGroups?.find((g) => g.ActivityAssignments.find((a) => a.PlayerID === playerId))?.GroupName;
    return group ? ` / ${group}` : '';
};

const CalendarEvents = ({ teamId, playerId, isPitcher, isCatcher }) => {
    const { t: translate } = useTranslation();
    const { data, status } = useOktaQuery(
        ['upcomingschedule', { teamId, playerId }],
        (accessToken) => querySchedule(accessToken, teamId, playerId),
    );

    const days = [{ title: translate('common.today'), date: TODAY, isTomorrow: false }, { title: translate('common.tomorrow'), date: moment(TODAY).add(1, 'd'), isTomorrow: true }];

    if (status === 'loading') {
        return (
            <div className="d-flex py-5 justify-content-center">
                <LoadingSpinner />
            </div>
        );
    }

    return data && (
        <Card className="shadow-sm">
            {days.map((day) => (
                <div key={day.date}>
                    <DayHeader>{ day.title }</DayHeader>
                    <DaySchedule className="border rounded overflow-hidden m-2">
                        {(getWorkoutsForDate(data.workouts, day.date)?.length
                        + getActivitiesForDate(data.schedules, day, playerId, isPitcher, isCatcher).length) >= 1
                            ? (
                                <>
                                    {getWorkoutsForDate(data.workouts, day.date).map((workout) => (
                                        <li className="d-flex align-items-center" key={workout.PlayerWorkoutID}>
                                            <div className="h-100 d-flex align-items-stretch">
                                                <DayEventMarker color="#ffd700" />
                                                <div className="p-2 text-uppercase">{workout.Workout.Name}</div>
                                            </div>
                                        </li>
                                    ))}
                                    {orderedActivities(getActivitiesForDate(data.schedules, day, playerId, isPitcher, isCatcher)).map((activity) => (
                                        <li className="d-flex align-items-center justify-content-between" key={activity.DailyScheduleActivityID}>
                                            <div className="h-100 d-flex align-items-stretch">
                                                <DayEventMarker color="#0077d9" />
                                                <div className="p-2 text-uppercase">
                                                    {activity.Name}{getActivityGroupName(activity, playerId)}
                                                </div>
                                            </div>
                                            <div className="text-muted p-2 flex-shrink-0">
                                                {`${formatTime(activity.StartTime)}${activity.EndTime ? ` - ${formatTime(activity.EndTime)}` : ''}`}
                                            </div>
                                        </li>
                                    ))}
                                </>
                            )
                            : <div className="text-center text-muted p-2">{translate('home.no-events')}</div>}
                    </DaySchedule>
                </div>
            ))}
        </Card>
    );
};

export default CalendarEvents;
